/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AccordionContext } from '@/components/blocks/SideBar';
import { FacetNavigationEntry } from '@/components/content/FacetNavigation/parts/Entry';
import { FacetNavigationPrice } from '@/components/content/FacetNavigation/parts/Price';
import { facetNavigationAccordionSX } from '@/components/content/FacetNavigation/styles/accordion';
import { facetNavigationAccordionSummarySX } from '@/components/content/FacetNavigation/styles/accordionSummary';
import { facetNavigationGridItemSX } from '@/components/content/FacetNavigation/styles/gridItem';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { useLocalization } from '@/data/Localization';
import { FACET_CATEGORY_VALUE, FACET_PRICE_PREFIX } from '@/data/constants/facet';
import { ContentContext } from '@/data/context/content';
import { useDeviceType } from '@/data/deviceDetection';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { FC, SyntheticEvent, useCallback, useContext, useState } from 'react';
import { categoryNavSX, catNavCategorySX } from '@/components/content/CategoryNavigationRv/style/container';
import { Linkable } from '@/components/blocks/Linkable';
import { ShowMoreLess } from '@/components/content/FacetNavigation/parts/ShowMoreLess';

export const FacetNavigationItemChange: FC = () => {
	const productFilterNLS = useLocalization('ProductFilter');
	const categoryNLS = useLocalization("Category");
	const { toggleFacetLimit, facets } = useContext(ContentContext) as ReturnType<
		typeof useFacetNavigation
	>;
	const { toggleOpen } = useContext(AccordionContext) as { toggleOpen: () => void };
	const { isMobile } = useDeviceType();
	const [activeFacetId, setActiveFacetId] = useState<string>(() => '');
	const handleAccordionChange = useCallback(
		(facetValue: string) => (_event: SyntheticEvent<Element, Event>, isExpanded: boolean) => {
			isMobile && setActiveFacetId(isExpanded ? facetValue : '');
		},
		[isMobile]
	);
	// const filteredFacets = useMemo(() => facets?.filter((facetItem) => facetItem.name !== 'ParentCatalogGroup') ?? [], [facets]);
	return (
		<>
			{facets.map((facetItem) => (
				<Accordion
					id={`productFilter-facet-${facetItem.name}`}
					data-testid={`productFilter-facet-${facetItem.name}`}
					defaultExpanded={!isMobile}
					key={`productFilter-facet-${facetItem.value}`}
					expanded={!isMobile || activeFacetId === facetItem.value}
					onChange={handleAccordionChange(facetItem.value)}
					square={true}
					sx={facetNavigationAccordionSX}
				>
					<AccordionSummary
						aria-controls={`${facetItem.value}-content`}
						id={`${facetItem.value}-header`}
						data-testid={`${facetItem.value}-header`}
						sx={facetNavigationAccordionSummarySX}
						expandIcon={isMobile ? <ExpandMoreIcon /> : null}
					>
						<Typography variant="body2">
							{facetItem.value.startsWith(FACET_PRICE_PREFIX) ? productFilterNLS.Labels.price.t()
							: facetItem.value.startsWith(FACET_CATEGORY_VALUE) ? categoryNLS.Category.t()
							: facetItem.extendedData?.fname ?? facetItem.name}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{facetItem.name === 'ParentCatalogGroup' ? (
							<>
								<Grid container component="nav" aria-label={categoryNLS.SubCategoryNavARIALABEL.t()}>
									{facetItem.entry?.map((entry: any) => (
										<Grid width="100%" sx={catNavCategorySX}>
											<Linkable href={entry.x_seoPath} role="link" aria-label={`${entry.label} (${entry.count} ${productFilterNLS.Labels.artikelen.t()})`} sx={categoryNavSX}>
												<Typography aria-hidden="true">{entry.label}</Typography>
												<Typography aria-hidden="true">({entry.count})</Typography>
											</Linkable>
										</Grid>
									))}
								</Grid>
								{facetItem.entry && (facetItem.showMore || facetItem.showLess) ? (
									<ShowMoreLess {...{facetItem, toggleFacetLimit}} />
								) : null}
							</>
						) : facetItem.value.startsWith(FACET_PRICE_PREFIX) ? (
							<FacetNavigationPrice />
						) : facetItem.name !== 'ParentCatalogGroup' && (
							<Stack justifyContent="flex-start" alignItems="flex-start">
								<Grid container>
									{facetItem.entry?.map((entry: any) => (
										<Grid item key={entry.value} sx={facetNavigationGridItemSX(!entry.image)}>
											<FacetNavigationEntry entry={entry} />
										</Grid>
									))}
								</Grid>
								{facetItem.entry && (facetItem.showMore || facetItem.showLess) ? (
									<ShowMoreLess {...{facetItem, toggleFacetLimit}} />
								) : null}
							</Stack>
						)}
						{isMobile && !facetItem.value.startsWith(FACET_PRICE_PREFIX) && facetItem.name !== 'ParentCatalogGroup' ?
							<Button
								variant="contained"
								sx={{ width: '100%' }}
								onClick={toggleOpen}
							>
								{productFilterNLS.Actions.ShowResults.t()}
							</Button>
							: null}
					</AccordionDetails>
				</Accordion>
			))}
		</>
	);
};
